import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { PageHeader } from '@/components/shared/PageHeader';
import { Breadcrumbs } from '@/components/shared/Breadcrumbs';
import { DataTablePagination } from '@/components/shared/DataTablePagination';
import { Button } from '@/components/ui/button';
import {
  ClientStaticQuickWithdrawDialog,
  ClientStaticWalletFilters,
  ClientStaticWalletReplaceDialog,
  ClientStaticWalletTable,
} from '@/components/features/wallets';
import {
  useBannedClientStaticWithdrawStatus,
  useClientStaticWallets,
  useTouchClientStaticWallet,
} from '@/lib/hooks';
import { useDebounce } from '@/lib/hooks/useDebounce';
import { useToast } from '@/lib/hooks/useToast';
import { ROUTES } from '@/config/routes';
import type {
  ClientStaticWallet,
  ClientStaticWalletFilters as ClientStaticWalletFiltersType,
} from '@/lib/types';

export const Route = createFileRoute('/_auth/_admin/admin/wallets/client-static/')({
  component: ClientStaticWalletsComponent,
});

function ClientStaticWalletsComponent() {
  const [filters, setFilters] = useState<ClientStaticWalletFiltersType>({});
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [replaceWallet, setReplaceWallet] = useState<ClientStaticWallet | null>(null);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [touchingId, setTouchingId] = useState<number | null>(null);

  const debouncedSearch = useDebounce(filters.search, 400);

  const { data, isLoading } = useClientStaticWallets({
    owner_id: filters.owner_id,
    search: debouncedSearch,
    page,
    per_page: perPage,
  });

  const { data: withdrawStatus } = useBannedClientStaticWithdrawStatus();

  const touchWallet = useTouchClientStaticWallet();
  const { success, error } = useToast();

  const handleTouch = (wallet: ClientStaticWallet) => {
    setTouchingId(wallet.id);
    touchWallet.mutate(wallet.id, {
      onSuccess: () =>
        success(
          'Дата использования обновлена',
          'Адрес будет проверен на депозиты в течение ~2 минут'
        ),
      onError: (err) => error('Не удалось обновить дату', err.message),
      onSettled: () => setTouchingId(null),
    });
  };

  const breadcrumbs = [
    { label: 'Кошельки', href: ROUTES.ADMIN.WALLETS },
    { label: 'Кошельки клиентов' },
  ];

  return (
    <div className="space-y-6">
      <Breadcrumbs items={breadcrumbs} />

      <PageHeader
        title="Кошельки клиентов"
        description="Персональные адреса по client_id"
        action={
          <Button onClick={() => setWithdrawOpen(true)}>
            Быстрый вывод{withdrawStatus?.state === 'running' ? ' (идёт...)' : ''}
          </Button>
        }
      />

      <ClientStaticWalletFilters
        filters={filters}
        onFiltersChange={(next) => {
          setFilters(next);
          setPage(1);
        }}
      />

      <ClientStaticWalletTable
        wallets={data?.data || []}
        onReplace={setReplaceWallet}
        onTouch={handleTouch}
        touchingId={touchingId}
        isLoading={isLoading}
      />

      {data?.meta && (
        <DataTablePagination
          meta={data.meta}
          onPageChange={setPage}
          onPerPageChange={(value) => {
            setPerPage(value);
            setPage(1);
          }}
        />
      )}

      <ClientStaticWalletReplaceDialog
        open={replaceWallet !== null}
        onOpenChange={(open) => {
          if (!open) setReplaceWallet(null);
        }}
        wallet={replaceWallet}
      />

      <ClientStaticQuickWithdrawDialog open={withdrawOpen} onOpenChange={setWithdrawOpen} />
    </div>
  );
}
